import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { getAuthSession, subscribeAuthStateChange } from './auth'
import { hasPendingAuthCallbackTokens } from './authCallback'
import { captureBillingIntent, consumeBillingIntent } from './billingIntent'

import { getUserId } from '@/api/user'
import Button from '@/components/Button'
import { AppDispatch } from '@/store'
import {
  getEnterpriseThunk,
  getSubscriptionThunk,
  selectCurrentEnterprise,
  selectEnterpriseError,
  selectEnterpriseLoaded,
  setUser,
} from '@/store/features/user/userSlice'
import { resetTenantState } from '@/store/resetTenantState'
import { isPublicPath } from '@/utils/publicPaths'
import { clearSentryUser, setSentryUser } from '@/utils/sentry'

const getSessionIdentity = (session: Awaited<ReturnType<typeof getAuthSession>>) =>
  session ? JSON.stringify([session.user.id ?? session.user.email, session.user.email]) : null

export default function AuthCheck() {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(true)
  const enterprise = useSelector(selectCurrentEnterprise)
  const enterpriseLoaded = useSelector(selectEnterpriseLoaded)
  const enterpriseError = useSelector(selectEnterpriseError)
  const lastAuthIdentity = useRef<string | null>(null)
  // Refs let the long-lived auth listener read the latest path without putting
  // `location.pathname` in its deps and forcing teardown/resubscribe on every
  // navigation (which would drop any SIGNED_IN landing in the resubscribe gap).
  const pathnameRef = useRef(location.pathname)
  useEffect(() => {
    pathnameRef.current = location.pathname
  })

  const landAfterAuth = () => navigate(consumeBillingIntent() ?? '/app')

  useEffect(() => {
    const handleAuthCheck = async () => {
      try {
        if (hasPendingAuthCallbackTokens()) return
        const session = await getAuthSession()
        const onPublicPath = isPublicPath(location.pathname)

        if (!session) {
          if (!onPublicPath) {
            captureBillingIntent(location.pathname)
            navigate('/login')
          }
          return
        }

        const sessionIdentity = getSessionIdentity(session)
        if (!lastAuthIdentity.current || lastAuthIdentity.current !== sessionIdentity) {
          let newUserId: string | null = null
          // Don't block the app if we can't get the user id, we only use it for analytics
          try {
            const { data: userIdData } = await getUserId()
            newUserId = userIdData.user_id
          } catch (error) {
            console.error(`Error getting user id: ${error}`)
          }

          dispatch(
            setUser({
              userId: newUserId,
              avatarUrl: session.user.avatarUrl,
              email: session.user.email,
              name: session.user.name,
            })
          )
          setSentryUser({ id: newUserId ?? session.user.id ?? session.user.email, email: session.user.email }) // set Sentry user context for error tracking
        } else {
          dispatch(
            setUser({
              avatarUrl: session.user.avatarUrl,
              email: session.user.email,
              name: session.user.name,
            })
          )
        }
        lastAuthIdentity.current = sessionIdentity

        // Wait for the fetch before routing — `enterprise` no longer persists,
        // so a cold-load read would misroute everyone to /get-started.
        if (!enterpriseLoaded) {
          return
        }

        // A transient `/api/v1/enterprise` failure must not redirect a signed-in
        // tenant user into onboarding — hold the route until the fetch retries.
        if (enterpriseError) {
          return
        }

        // If user is authenticated but on login/forgot-password page, redirect them appropriately
        if (onPublicPath) {
          try {
            if (enterprise?.subscription?.status === 'active') {
              // User has an active enterprise subscription, redirect to app
              landAfterAuth()
            } else if (enterprise?.id) {
              // User has an enterprise but no active subscription
              // navigate('/payment')
              // Teams Refresh: Easiest way to remove paywall is just redirecting to the app instead
              landAfterAuth()
            } else {
              // User exists but needs to create an enterprise
              navigate('/get-started')
            }
          } catch (e) {
            console.error('Auth check redirect failed', e)
            // Some bug while trying to get user's enterprise
            navigate('/get-started')
          }
          return
        }

        // If user is on other non-app pages (get-started, payment), check their enterprise status
        if (!location.pathname.startsWith('/app')) {
          try {
            if (enterprise?.subscription?.status === 'active') {
              // User has an active enterprise subscription, redirect to app
              landAfterAuth()
            } else if (enterprise?.id) {
              // User has an enterprise but no active subscription

              // navigate('/payment')
              // Teams Refresh: Easiest way to remove paywall is just redirecting to the app instead
              landAfterAuth()
            } else {
              // User exists but needs to create an enterprise
              navigate('/get-started')
            }
          } catch (e) {
            console.error('Auth check enterprise status failed', e)
            // Some bug while trying to get user's enterprise
            navigate('/get-started')
          }
          return
        }

        // If user is on app routes but has no enterprise or inactive subscription
        if (location.pathname.startsWith('/app')) {
          if (!enterprise?.id) {
            // No enterprise, redirect to get-started
            captureBillingIntent(location.pathname)
            navigate('/get-started')
          }
        }
      } catch (error) {
        console.error('Auth check session error', error)
        captureBillingIntent(location.pathname)
        navigate('/login')
      } finally {
        setIsLoading(false)
      }
    }

    window.addEventListener('auth-callback-complete', handleAuthCheck)
    handleAuthCheck()
    return () => window.removeEventListener('auth-callback-complete', handleAuthCheck)
  }, [dispatch, navigate, location.pathname, enterprise, enterpriseLoaded, enterpriseError])

  // Auth listener lives in its own effect so it isn't torn down every time the
  // bootstrap deps change. Refetching is keyed off `lastAuthIdentity` instead of
  // `enterpriseLoaded` so a cross-tab user switch (which leaves
  // `enterpriseLoaded` true from the previous session) still triggers a fetch.
  useEffect(() => {
    const subscription = subscribeAuthStateChange((event, session) => {
      if (hasPendingAuthCallbackTokens()) {
        if (event === 'SIGNED_IN' && session?.provider === 'workos') {
          lastAuthIdentity.current = null
          resetTenantState(dispatch, 'session')
        }
        return
      }
      if (!session) {
        lastAuthIdentity.current = null
        resetTenantState(dispatch, 'session')
        clearSentryUser()
        if (!isPublicPath(pathnameRef.current)) {
          navigate('/login')
        }
        return
      }

      if (event === 'SIGNED_IN' && getSessionIdentity(session) !== lastAuthIdentity.current) {
        lastAuthIdentity.current = null
        resetTenantState(dispatch, 'session')
        dispatch(getEnterpriseThunk({ needSubscription: false }))
      }
      dispatch(getSubscriptionThunk())
    })

    return () => subscription.unsubscribe()
  }, [dispatch, navigate])

  if (isLoading) {
    return null
  }

  // Any route whose redirect decision depends on enterprise state must wait
  // for the fetch — otherwise a signed-in user cold-loading e.g. /get-started
  // flashes the page before AuthCheck redirects them to /app.
  const enterpriseDependentPaths = ['/app', '/get-started', '/payment']
  const dependsOnEnterprise = enterpriseDependentPaths.some((p) => location.pathname.startsWith(p))
  if (dependsOnEnterprise && !enterpriseLoaded) {
    return null
  }

  // Cold-load failure: enterprise fetch errored and we have no cached data to
  // fall back on. The bootstrap effect won't retry on its own and the route
  // won't redirect (the `enterpriseError` gate holds it), so surface a retry
  // affordance instead of a permanent blank screen.
  if (dependsOnEnterprise && enterpriseError && !enterprise) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <p style={{ maxWidth: '420px' }}>
          We couldn&apos;t load your organization. Check your connection and try again.
        </p>
        {/* Button has align-self: stretch in its SCSS; browsers treat that as
            flex-start when the item has an explicit width, so wrap it in a
            div that inherits the parent's align-items: center. */}
        <div>
          <Button
            title="Retry"
            type="dark"
            width={120}
            onClick={() => dispatch(getEnterpriseThunk({ needSubscription: false }))}
          />
        </div>
      </div>
    )
  }

  return (
    <>
      <Outlet />
    </>
  )
}
